import { HideContainer } from '@components/hide-container';
import { IconButton } from '@components/icon-button';
import { IconEdit } from '@components/icons/edit';
import { IconLoopExit } from '@components/icons/loop-exit';
import { Spinner } from '@components/loading-spinner';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import {
  useJourneyTravellerAction,
  useUpdateJourneyTravellerAction,
} from 'api/traveller-actions/hooks';
import { TravellerJourneyActionDetailed } from 'api/types';
import { getActionStatus } from 'ui/authenticated/main/helpers';

import {
  ActionProgressBar,
  BaseContentProps,
  EditableActionContent,
  isActionError,
  SidebarProps,
  TravellerActionActions,
  useEditingContext,
} from '../common';
import { ConditionFormContent } from '../condition/helpers';
import { getConditionDescription } from '../condition/journey-node';

type ActionType = 'break_loop';

export const SidebarContent = ({ shortId }: SidebarProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const travellerActionQuery = useJourneyTravellerAction<ActionType>(shortId);
  if (travellerActionQuery.isLoading) {
    return <Spinner />;
  }
  if (!shortId || !travellerActionQuery.data) {
    return <div>{t('dataNotFound')}</div>;
  }

  const action = travellerActionQuery.data;

  return (
    <Sidebar>
      <Sidebar.SimpleHeader title={t('breakLoop')} icon={IconLoopExit} iconColor="elementRed100">
        <TravellerActionActions action={action} />
      </Sidebar.SimpleHeader>
      <Sidebar.Content>
        <EditableActionContent
          edit={<EditContent action={action} />}
          display={<DetailsContent action={action} />}
        />
      </Sidebar.Content>
    </Sidebar>
  );
};

type ContentProps = BaseContentProps<TravellerJourneyActionDetailed<ActionType>>;

const EditContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { stopEditing } = useEditingContext();

  const editMutation = useUpdateJourneyTravellerAction(action, {
    onSuccess: stopEditing,
  });

  return (
    <ConditionFormContent
      action={action}
      conditionDescription={t('breakLoop')}
      journeyId={action.journeyId}
      hideActionButtons
      onSubmit={(data) =>
        editMutation.mutateAsync({
          values: {
            loopAction: action.data.loopAction,
            ...data,
          },
        })
      }
      onCancel={stopEditing}
    />
  );
};

const DetailsContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const isError = isActionError(action);
  const status = getActionStatus(action);
  const { startEditing } = useEditingContext();

  const progressText =
    status === 'completed'
      ? t('completed')
      : isError
      ? t('error')
      : status === 'in_progress'
      ? t('inProgress')
      : t('notStarted');

  return (
    <Stack space={6}>
      <HideContainer title={t('actionStatus')}>
        <ActionProgressBar
          action={action}
          progressText={progressText}
          isError={isError}
          dateSuccess={action.result?.dateSucceeded}
          isCard={false}
          progressPercentage=" "
        />
      </HideContainer>
      <Sidebar.Separator />
      <HideContainer title={t('actionConditions')}>
        <Text font="regular14" text={t('breakLoopDetailsDescription')} />
        <Text
          font="semibold14"
          text={getConditionDescription({
            values: action.values,
            inNode: false,
          })}
        />
      </HideContainer>
      <Sidebar.Separator />
      {status !== 'completed' && (
        <Sidebar.Footer>
          <IconButton variant="ghost-purple" icon={<IconEdit />} onClick={startEditing} />
        </Sidebar.Footer>
      )}
    </Stack>
  );
};

const sidebarLocale = {
  en: {
    completed: 'Completed',
    inProgress: 'In progress',
    error: 'Error',
    notStarted: 'Not started',
    dataNotFound: 'Data not found',
    actionStatus: 'Action status',
    actionConditions: 'Conditions',
    breakLoopDetailsDescription: 'The exit loop action is configured to exit when',
    breakLoopDescription: 'Exit the loop when specific conditions are met',
    breakLoopDescriptionType: 'Exit loop if {conditionType} of the following matches',
    breakLoop: 'Exit loop',
  },
};
